/**
 * The recruiter's desk, shown as chips above a list — each dimension removable
 * on its own.
 *
 * Scope is a FILTER that happens to be on by default, not an invisible server
 * rule: a recruiter must see what is being applied and be able to step past it,
 * one dimension at a time. "Singapore + Technology" finds 6 companies; dropping
 * the sector chip finds 46; dropping both finds 1,882. Those are three real
 * questions a recruiter asks, and an all-or-nothing toggle could only answer the
 * first and the last.
 *
 * `dims` is the caller's state, not ours, because the LIST has to be refetched
 * when it changes — the bar cannot own a value the page must read. It takes the
 * four values the API understands:
 *
 *   both       the whole desk (the default, and what the server assumes when the
 *              client sends no parameter at all)
 *   territory  sector chip removed
 *   sector     territory chip removed
 *   none       "Show all"
 *
 * Widening is deliberately not persisted anywhere: every visit opens on the
 * desk, so one click last month cannot leave someone in the firehose for weeks.
 */
const CHIP_ON = 'inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800';
const CHIP_DEAD = 'inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-sm text-amber-800';

export default function ScopeFilterBar({ scope, dims = 'both', onChange }) {
  if (!scope) return null;

  const territories = scope.territories || [];
  const sectors = scope.sectors || [];
  if (territories.length === 0 && sectors.length === 0) return null;

  const territoryOn = dims === 'both' || dims === 'territory';
  const sectorOn = dims === 'both' || dims === 'sector';

  // Removing one dimension leaves the other applied; removing the last one is
  // the same state "Show all" produces, so the two paths converge rather than
  // being two different kinds of unfiltered.
  const drop = (kind) => {
    if (kind === 'territory') onChange(sectorOn ? 'sector' : 'none');
    else onChange(territoryOn ? 'territory' : 'none');
  };

  if (dims === 'none') {
    return (
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
        <span>Showing all — not filtered to your desk.</span>
        <button type="button" onClick={() => onChange('both')} className="text-blue-600 underline hover:text-blue-800">
          Back to my desk
        </button>
      </div>
    );
  }

  const chip = (kind, label, entries) => (
    <span className={entries.every((e) => e.matching) ? CHIP_ON : CHIP_DEAD}>
      <span className={entries.every((e) => e.matching) ? 'text-blue-500' : 'text-amber-500'}>{label}:</span>
      {entries.map((e) => e.name).join(', ')}
      {/* A label the server dropped is shown, not hidden: this is the one place
          a recruiter finds out their desk is misspelt or unenriched. */}
      {!entries.every((e) => e.matching) && <span className="text-xs italic">— matches no records yet</span>}
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        onClick={() => drop(kind)}
        className="ml-1 font-bold opacity-60 hover:opacity-100"
      >
        ×
      </button>
    </span>
  );

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-sm text-gray-500">Your desk:</span>
      {territoryOn && territories.length > 0 && chip('territory', 'Territory', territories)}
      {sectorOn && sectors.length > 0 && chip('sector', 'Sector', sectors)}

      {/* Only offered when something is actually being narrowed — a "Show all"
          that widens nothing is a button that lies. */}
      {scope.applied && (
        <button type="button" onClick={() => onChange('none')} className="text-sm text-blue-600 underline hover:text-blue-800">
          Show all
        </button>
      )}
      {/* Visible only once a dimension has been dropped, so the recruiter is
          never stranded in a half-desk with no way back. */}
      {dims !== 'both' && (
        <button type="button" onClick={() => onChange('both')} className="text-sm text-gray-500 underline hover:text-gray-700">
          Reset to my desk
        </button>
      )}
    </div>
  );
}
